import { IncomingMessage, ServerResponse } from 'http'
import { Duration } from 'luxon'
import { NextPage } from 'next'
import { Case, Default, Switch } from 'react-if'
import { paths } from '@/constants/paths'
import { projectTypes } from '@/constants/projectTypes'
import { publisher, releasedEvent } from '@/constants/seo'
import { slugs } from '@/constants/slugs'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { ServerExperiments } from '@/experimentation'
import { ExperimentParamsArray, runExperimentsOnServer } from '@/experimentation/server'
import { Seo, useDataForEpisode } from '@/layout/Seo'
import { MovieJsonLd, PodcastEpisodeJsonLd, TVEpisodeJsonLd } from '@/layout/Seo/JsonLd'
import { VideoObject } from '@/layout/Seo/JsonLd/types'
import { getVideoObjects } from '@/layout/Seo/ProjectSeoHelper'
import { getEpisodeTitle } from '@/layout/Seo/SeoHelper'
import { GetAppBannerProvider } from '@/organisms/GetAppBanner'
import { getFirstTitleRating, getTitleGenres } from '@/services/ContentCatalog'
import { createEpisodeDeepLink } from '@/services/DeepLinkService'
import { getGuildStatus } from '@/services/GuildUserService/GuildUserService'
import {
  getSkipCategoryPreferencesForProject,
  SerializedSkipCategoryPreference,
} from '@/services/ParentalControlsService'
import { isSellingTickets, isStreaming } from '@/services/ProjectPhaseService'
import {
  Episode,
  fetchTheatricalReleaseMono,
  getContentEpisode,
  getEpisode,
  getProjectAbout,
  Project,
  ProjectMeta,
} from '@/services/ProjectsService'
import { findSlugByEpisodeGuid } from '@/services/ProjectsService/FriendlyEpisodeSlugHelper'
import { ContentEpisode } from '@/services/ProjectsService/types'
import { getSuggestedShowsAndFilms } from '@/services/RecommendationService/RecommendationService'
import { SuggestedShowsAndFilmsContent } from '@/services/RecommendationService/types'
import { getShopifyConfigurationForProject } from '@/services/ShopifyConfigurationService'
import {
  resolveShopifyConfig,
  ShopifyConfigFields,
} from '@/services/ShopifyConfigurationService/ShopifyConfigurationService'
import { TicketsStatusReference } from '@/types/codegen-federation'
import { buildDecorativeEpisodePath } from '@/utils/EpisodeUtil'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { logger } from '@/utils/logging'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { shouldHideEpisodeFromSearchEngines, shouldHideProjectFromSearchEngines } from '@/utils/seo/searchEngineHelper'
import { getStreamingArgsFromGuildStatus } from '@/utils/streaming/streaming-args'
import { loadTranslations } from '@/utils/translate/translate-server'
import { PerfLogsProvider } from '@/utils/usePerfLogs'
import { WatchEpisodeView } from '@/views/WatchEpisodeView'

interface WatchEpisodePageProps {
  deepLink: string | null
  episode: Episode
  hasPayItForward: boolean
  locale: string
  project: Project
  shopifyStore?: ShopifyConfigFields | null
  isGuildMember?: boolean
  isSellingTickets: boolean
  canViewUnauthed?: boolean
  experiments: ServerExperiments
  userId?: string | null
  parentalControlsTotal: number
  showParentalControls: boolean
  initialPreferences: SerializedSkipCategoryPreference[]
  contentEpisode?: ContentEpisode
  suggestedShowsAndFilms: SuggestedShowsAndFilmsContent
}

export const WatchEpisodePage: NextPage<WatchEpisodePageProps & ProjectMeta> = (props) => {
  const {
    episode,
    project,
    hasPayItForward,
    isGuildMember,
    isSellingTickets,
    canViewUnauthed,
    userId,
    shopifyStore,
    deepLink,
    locale,
    parentalControlsTotal,
    showParentalControls,
    initialPreferences,
    contentEpisode,
    suggestedShowsAndFilms,
  } = props
  const { meta } = useDataForEpisode(
    {
      logoCloudinaryPath: project.logoCloudinaryPath,
      name: project.name,
      projectType: project.projectType,
      slug: project.slug,
    },
    episode,
    locale,
    contentEpisode,
  )
  const url = meta.canonical
  const duration = contentEpisode?.duration ?? episode?.source?.duration ?? 0
  const description = contentEpisode?.description ?? episode.description
  const name = contentEpisode?.name ?? episode.subtitle
  const thumbnailUrl = meta?.openGraph?.images?.[0]?.url
  const releaseDate = contentEpisode?.originalReleaseDate ?? episode.releaseDate
  const contentRating = getFirstTitleRating(project?.title)
  const genres = getTitleGenres(props.title)
  const episodeNumber =
    contentEpisode && 'episodeNumber' in contentEpisode ? contentEpisode.episodeNumber : episode.episodeNumber
  const seasonNumber =
    contentEpisode && 'season' in contentEpisode ? contentEpisode?.season?.seasonNumber : episode.seasonNumber
  const video: VideoObject = {
    type: 'VideoObject',
    contentUrl: url,
    description,
    duration: duration
      ? Duration.fromDurationLike({ hour: 0, minute: 0, second: duration }).normalize().toISO()
      : undefined,
    name,
    thumbnailUrl,
    uploadDate: releaseDate || '2021-03-25T12:00:00Z',
  }

  const shouldHide = Boolean(shouldHideEpisodeFromSearchEngines(episode) || shouldHideProjectFromSearchEngines(project))
  return (
    <>
      <Seo
        {...meta}
        friendlyPath={buildDecorativeEpisodePath(
          {
            seasonNumber,
            episodeNumber,
            subtitle: episode.subtitle,
          },
          { disableDecoration: false },
        )}
        nofollow={shouldHide}
        noindex={shouldHide}
        path={`/watch/${project.slug}/episode/${episode.guid}`}
      />
      <Switch>
        <Case condition={project.projectType === projectTypes.movie}>
          <MovieJsonLd
            contentRating={contentRating}
            dateCreated={releaseDate}
            description={description}
            genre={genres ?? []}
            image={thumbnailUrl}
            name={getEpisodeTitle({ project, episode })}
            publisher={publisher}
            releasedEvent={releasedEvent}
            trailer={getVideoObjects(project.videos)}
            url={url}
            video={video}
          />
        </Case>
        <Case condition={project.projectType === projectTypes.podcast}>
          <PodcastEpisodeJsonLd
            contentRating={contentRating}
            description={description}
            episodeNumber={episodeNumber}
            genre={genres ?? []}
            image={thumbnailUrl}
            name={getEpisodeTitle({ project, episode })}
            partOfSeries={{
              type: 'CreativeWorkSeries',
              description: props.title?.mediumDescription || props.title?.shortDescription || '',
              name: project.name,
              url: `${paths.getBaseURL()}${paths.watch.index}/${episode.projectSlug}`,
            }}
            publisher={publisher}
            releasedEvent={releasedEvent}
            url={url}
            video={video}
          />
        </Case>
        <Default>
          <TVEpisodeJsonLd
            contentRating={contentRating}
            description={description}
            episodeNumber={episode.episodeNumber}
            genre={genres ?? []}
            image={thumbnailUrl}
            name={getEpisodeTitle({ project, episode })}
            partOfSeason={{
              type: 'TVSeason',
              name: `${project.name} - Season ${seasonNumber}`,
              seasonNumber,
            }}
            partOfSeries={{
              type: 'TVSeries',
              description: props.title?.mediumDescription || props.title?.shortDescription || '',
              name: project.name,
              url: `${paths.getBaseURL()}${paths.watch.index}/${episode.projectSlug}`,
            }}
            publisher={publisher}
            releasedEvent={releasedEvent}
            url={url}
            video={video}
          />
        </Default>
      </Switch>
      <PerfLogsProvider>
        <GetAppBannerProvider deepLink={deepLink} projectSlug={project.slug} context="episode">
          <ProjectMetaProvider project={props}>
            <WatchEpisodeView
              projectType={project.projectType}
              hasPayItForward={hasPayItForward}
              episodeId={contentEpisode?.id ?? episode.guid}
              projectSlug={contentEpisode?.project?.slug ?? episode.projectSlug}
              projectName={project.name}
              episode={episode}
              skipAds={!!episode?.isTrailerM3u8}
              shopifyStore={shopifyStore}
              isGuildMember={isGuildMember}
              isSellingTickets={isSellingTickets}
              canViewUnauthed={canViewUnauthed}
              userId={userId}
              parentalControlsTotal={parentalControlsTotal}
              showParentalControls={showParentalControls}
              initialPreferences={initialPreferences}
              contentEpisode={contentEpisode}
              suggestedShowsAndFilms={suggestedShowsAndFilms}
              autoplay
            />
          </ProjectMetaProvider>
        </GetAppBannerProvider>
      </PerfLogsProvider>
    </>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler<WatchEpisodePageProps>(
  async ({ federationClient, locale, next404, nextRedirect, params, preview, req, res, resolvedUrl, userId }) => {
    const projectSlug = getItemOrFirstEntry(params.project)
    if (!projectSlug) return next404()
    if (projectSlug === slugs.theChosen) return nextRedirect(paths.watch.index)

    const projectWatchPath = `${paths.watch.index}/${params.project}`

    const pathParts = params.friendlyPath
    if (!Array.isArray(pathParts)) return next404()

    const guid = pathParts[0]
    if (!guid) {
      logger().warn(`No episode guid provided`, { projectSlug, pathParts })
      return nextRedirect(projectWatchPath)
    }

    if (projectSlug === slugs.dryBar) {
      const correctSlug = await findSlugByEpisodeGuid(federationClient, guid)
      if (correctSlug && correctSlug !== projectSlug) {
        return nextRedirect(resolvedUrl.replace(projectSlug, correctSlug))
      }
    }

    const [guildStatus, translations] = await Promise.all([
      getGuildStatus(federationClient),
      loadTranslations(locale, ['common', 'watch', 'app-promo', 'ads', 'account', 'guild', 'parental-controls']),
    ])

    const streamingArgs = getStreamingArgsFromGuildStatus(guildStatus)

    const [episodeResponse, contentEpisode] = await Promise.all([
      getEpisode({ guid, projectSlug }, federationClient, streamingArgs),
      getContentEpisode({ guid }, federationClient, streamingArgs),
    ])

    if (!episodeResponse || !episodeResponse.episode || !episodeResponse.project) {
      const ipAddress = req.socket.remoteAddress
      const referer = req.headers['referer']
      const context = { guid, projectSlug, episodeResponse, ipAddress, referer }
      logger().error(`Episode not found for guid=${guid} and projectSlug=${projectSlug}`, context)

      return nextRedirect(projectWatchPath)
    }

    const path = `${paths.watch.index}/${projectSlug}/episode/${guid}`
    // Grab all Dry Bar Merch for Specials
    const isSpecial = contentEpisode?.episode?.__typename === 'ContentSpecial'
    const shopifyUrl = isSpecial ? slugs.dryBar : projectSlug

    const [projectMeta, shopifyStoreFromContentful, deepLink] = await Promise.all([
      getProjectAbout({ slug: projectSlug }, federationClient, { locale, preview }),
      getShopifyConfigurationForProject(shopifyUrl, { locale, preview }),
      createEpisodeDeepLink(
        {
          path,
          projectSlug,
          guid,
          projectName: contentEpisode?.projectName ?? episodeResponse?.project?.name,
          contentName: contentEpisode?.episode?.name ?? episodeResponse?.episode?.subtitle,
          audioLocale: locale,
          captionLocale: locale,
        },
        {
          campaign: 'prompt-app-install',
          feature: 'app-prompt',
          channel: 'angel-web',
        },
      ),
    ])

    const shopifyStore = resolveShopifyConfig(shopifyStoreFromContentful, isSpecial)

    const canViewUnauthed = getCanViewUnauthed(userId, contentEpisode?.episode)
    const hasPayItForward = getHasPayItForward(contentEpisode) && !isStreaming(contentEpisode?.episode)
    if (!projectMeta) {
      logger().warn(`No project meta found for projectSlug=${projectSlug}`, { projectSlug, locale, preview })
      return next404()
    }

    const hasStreamableContent = isStreaming(contentEpisode?.episode)

    const [skipCategoryPreferences, theatricalReleaseMono, suggestedShowsAndFilms] = await Promise.all([
      getSkipCategoryPreferencesForProject({
        client: federationClient,
        contentId: episodeResponse.episode.id,
        lang: locale,
      }),
      fetchTheatricalReleaseMono({
        projectType: projectMeta.projectType,
        theatricalSlug: projectSlug,
        hasStreamableContent,
        client: federationClient,
      }),
      projectMeta?.title?.id ? getSuggestedShowsAndFilms(projectMeta.title.id, federationClient) : Promise.resolve([]),
    ])

    const skipsEnabled = Boolean(skipCategoryPreferences?.hasParentalControls)
    const experiments = await runExperiments({ req, res, skipsEnabled })

    const ticketStatus: TicketsStatusReference | null = theatricalReleaseMono?.region?.ticketsStatus ?? null

    return {
      props: {
        deepLink,
        hasPayItForward,
        locale,
        shopifyStore,
        isGuildMember: guildStatus?.isGuildMember ?? false,
        canViewUnauthed,
        episode: episodeResponse.episode,
        project: episodeResponse.project,
        contentEpisode: contentEpisode?.episode,
        userId: userId ?? null,
        // DESIGN NOTE - break out derived value so that we don't leak unintentional flow phase info
        isSellingTickets: isSellingTickets(ticketStatus),
        parentalControlsTotal: skipCategoryPreferences.parentalControlsTotal,
        initialPreferences: skipCategoryPreferences.preferences,
        showParentalControls: Boolean(experiments['parental-controls']),
        suggestedShowsAndFilms: suggestedShowsAndFilms ?? [],
        experiments,
        ...projectMeta,
        ...translations,
      },
    }
  },
)

export default WatchEpisodePage

async function runExperiments({
  req,
  res,
  skipsEnabled,
}: {
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> }
  res: ServerResponse
  skipsEnabled: boolean
}): Promise<ServerExperiments> {
  const expConfigs: ExperimentParamsArray = []

  if (skipsEnabled) {
    expConfigs.push({ experimentKey: 'parental-controls', defaultValue: false })
  }

  const experiments = await runExperimentsOnServer(req, res, expConfigs)

  return experiments
}

export type PifEnabledEntity = { pifEnabled?: boolean | null }

export function getHasPayItForward(entity?: PifEnabledEntity | null): boolean {
  return Boolean(entity?.pifEnabled)
}

const FREE_UNAUTH_EPISODE_IDS = new Set<string>(['75c66550-f8fd-480a-8664-435cfffcfbf4'])

export function getCanViewUnauthed(
  userId: string | null | undefined,
  episode?: { __typename?: string | null; id?: string | null } | null,
): boolean {
  if (userId) return false
  const isSneakPeek = episode?.__typename === 'ContentSneakPeek'
  const isAllowlisted = episode?.id ? FREE_UNAUTH_EPISODE_IDS.has(episode.id) : false
  return isSneakPeek || isAllowlisted
}
